import {
  Component,
  input,
  signal,
  output,
  ElementRef,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import {NgClass} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  InvitationUploadDocumentResponse,
  CreateInvitationDocumentRequestParams,
  InvitationUploadDocumentEditRequest,
} from '@frontend/shared-api';
import {DocumentItemComponent} from '../document-item/document-item.component';
import {DialogService} from 'primeng/dynamicdialog';
import {UploadDocumentDialogComponent} from '../upload-document-dialog/upload-document-dialog.component';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {Tooltip} from 'primeng/tooltip';
import {InvitationUploadDocument} from '@frontend/directus';
import {DirectusTooltipComponent} from '../../../../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {ToastService} from '@frontend/toast';

// TODO: replace with the shared DocumentUploadCardComponent (app-document-upload-card)
@Component({
  selector: 'app-upload-document-card',
  imports: [
    NgClass,
    TranslocoPipe,
    DocumentItemComponent,
    TranslateDirectusPipe,
    Tooltip,
    DirectusTooltipComponent,
  ],
  providers: [TranslateDirectusPipe],
  templateUrl: './upload-document-card.component.html',
  styleUrls: ['./upload-document-card.component.scss'],
})
export class UploadDocumentCardComponent {
  private readonly dialogService = inject(DialogService);
  private readonly translateDirectus = inject(TranslateDirectusPipe);
  private readonly toastService = inject(ToastService);

  readonly directusInvitationUploadDocument = input.required<InvitationUploadDocument>();
  readonly uploadedDocuments = input<InvitationUploadDocumentResponse[]>([]);
  readonly isEditable = input.required<boolean>();

  readonly uploadDocument = output<CreateInvitationDocumentRequestParams>();
  readonly previewFile = output<number>();
  readonly downloadFile = output<number>();
  readonly editDocument = output<InvitationUploadDocumentEditRequest>();
  readonly deleteFile = output<number>();

  protected readonly hasError = signal<boolean>(false);

  protected readonly title = computed(() => {
    const current = this.directusInvitationUploadDocument();
    return this.translateDirectus.transform(current.translations, 'title');
  });

  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly hasDocuments = computed(() => this.uploadedDocuments().length > 0);
  protected readonly isCardMandatory = computed(
    () => this.directusInvitationUploadDocument().mandatory,
  );

  protected triggerUpload(): void {
    this.fileInput().nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processFile(input.files[0]);
    }
    input.value = '';
  }

  protected onEdit(document: InvitationUploadDocumentResponse): void {
    const ref = this.dialogService.open(UploadDocumentDialogComponent, {
      header: this.title(),
      modal: true,
      data: {
        isEdit: true,
        originalFilename: document.originalFileName,
        fileName: document.fileName,
        version: document.version,
        documentStatusUuid: document.documentStatusUuid,
      },
      closable: true,
      closeOnEscape: true,
    });

    ref?.onClose.subscribe((result: CreateInvitationDocumentRequestParams) => {
      if (result) {
        this.editDocument.emit({
          id: document.id,
          fileName: result.fileName,
          version: result.version,
          statusUuid: result.statusUuid,
        } as InvitationUploadDocumentEditRequest);
      }
    });
  }

  protected onFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  private processFile(file: File): void {
    this.hasError.set(false);
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.hasError.set(true);
      void this.toastService.showError('common.errors.pdfOnly', 'common.errors.pdfOnly');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      this.hasError.set(true);
      void this.toastService.showError('common.errors.fileTooLarge');
      return;
    }
    this.openMetadataDialog(file);
  }

  private openMetadataDialog(file: File): void {
    const ref = this.dialogService.open(UploadDocumentDialogComponent, {
      header: this.title(),
      modal: true,
      data: {
        file,
        invitationUploadDocumentUuid: this.directusInvitationUploadDocument().id,
      },
    });

    ref?.onClose.subscribe((result: CreateInvitationDocumentRequestParams) => {
      if (result) {
        this.uploadDocument.emit({
          ...result,
          invitationUploadDocumentUuid: this.directusInvitationUploadDocument().id,
        } as CreateInvitationDocumentRequestParams);
      }
    });
  }
}
